'use strict';

/* eslint function-paren-newline: ["error", "never"] */
define('vb/private/stateManagement/rules/basicRule',[
  'vb/private/log',
  'vb/binding/expression',
  'vb/private/utils',
  'vb/private/stateManagement/rules/rulesUtils',
  'vb/private/stateManagement/stateUtils',
],
(Log, Expression, Utils, RulesUtils, StateUtils) => {
  const logger = Log.getLogger('/vb/stateManagement/rules/basicRule');
  // supported value for the status property
  const RULE_STATUS_ENUM = {
    INACTIVE: 'inactive',
    ACTIVE: 'active',
  };

  // merges two flat string arrays
  const mergeArrays = (arr1 = [], arr2 = []) => arr2.reduce((acc, arr2Elem) => {
    if (arr1.findIndex((arr1Elem) => arr1Elem === arr2Elem) === -1) {
      acc.push(arr2Elem);
    }
    return acc;
  }, [...arr1]); // initialize the new Array with the contents of array1

  // coerces value to a boolean
  const coerceBoolean = (value) => {
    if (typeof value === 'string') {
      return (!(value.toLocaleLowerCase() === 'false' || value === '0'));
    }
    return Boolean(value);
  };

  /**
   * @param {string} ruleId id of the rule whose overlay attempted to read $env
   * @returns {Object} context object that rejects overlay reads of $env during `$withEnv` evaluation
   */
  const createInvalidOverlayEnvContext = (ruleId) => new Proxy({}, {
    get(target, property) {
      if (typeof property === 'symbol') {
        return target[property];
      }

      throw new Error(`Rule ${ruleId} cannot use $env in the value assigned to a constant. `
        + 'Use $env.row in the rule condition to choose when the value applies.');
    },
  });

  /**
   * Prunes the overlay to exclude constants that are disallowed and also determines the final targets
   * @param {String | Object} overlay property of a rule
   * @param {Array<string>} validTargets i.e. target interface constants that are allowed to be written back by
   * the rule
   * @returns {Object} modified overlay
   */
  const pruneValidTargets = (overlay, validTargets = []) => Object.fromEntries(Object.entries(overlay)
    .filter((op) => validTargets.includes(op[0])));

  /**
   * Matches expr against all allowed expressions in referencedContext props. It could reference any variable or
   * constant in any available scope. Example, a rule "referencedContext" property can refer to local scope
   * variables, or constants something from outer scope.
   * "referencedContext": {
   *   "generated": [
   *     "$page.variables.bar", "$application.variables.bar", "$constants.qux"
   *   ]
   * }
   *
   * @param expr
   * @param name
   * @param namespace
   * @returns {boolean}
   */
  const varNameMatch = (expr, name, namespace) => {
    // eslint-disable-next-line max-len
    const re = /(((\$base|\$global|\$extension|\$application|\$flow|\$page|\$fragment|\$layout)\.(constants|variables)\.)|(\$constants|\$variables)\.|(\$base\.(application|flow|page|fragment|layout)\.(constants|variables)\.))[\\$A-Z_a-z][\\$A-Z_a-z0-9]*$/g;
    const parts = re.exec(expr);
    if (parts && parts.length > 2) {
      const partial = parts[0].slice(parts[1].length);
      return partial === name && parts[1].indexOf(namespace) >= 0;
    }
    return false;
  };

  const EXPRESSION_TOKEN_CONSTANTS = 'constants';
  const EXPRESSION_TOKEN_$CONSTANTS = '$constants';

  /**
   * This class implements the basic rule behavior. A basic rule does two things-
   * i. it determines whether it can run by looking at the 'referencedContext' first followed by the 'condition'
   * properties
   * ii. if it can run, then it evaluates the overlay properties and builds a Map of values for one or more interface
   * constants
   *
   * A rule itself can be entered during initialization of the container, or when the rulesContext it drives off of
   * changes. When the rulesContext changes (i.e., a variable or constant that is part of the rulesContext for the
   * current container instance changes) then each rule can react to it by looking at the specific variable or
   * constant that changed and whether this change requires it to re-execute.
   *
   * For example, a page in extA might define an interface constant - 'ic', an interface variable - 'iv', a regular
   * constant - 'rc' and a regular variable - 'rv'. The interface constant 'ic' gets its value from rules.
   *
   * The rulesContext provided to all rules for the current container will be all four - ic, iv, rc, rv.
   * The specific ruleContext (singular) is a subset of these four variables/constants that the rule itself reacts to /
   * uses.
   *
   * So r1, r2 are two rules.
   * - r1 uses '$constants.rc' and returns value for '$constants.ic'
   * - r2 uses '$constants.iv', '$variables.rv' and returns value for '$constants.ic'
   *
   */
  class BasicRule {
    /**
     * Creates a rule.
     * @param {Object} ruleDef a single rule definition
     * @param {Object} container the current rule is associated to
     * @param {Array} targetExprs array of constants that are allowed to be targets
     */
    constructor(ruleDef, container, targetExprs = []) {
      this.log = logger;
      this.id = ruleDef.id;
      this.ruleDef = ruleDef;
      this.container = container;
      this.availableContexts = container.getAvailableContexts();
      this.targetExprs = targetExprs;
      this.referencedVars = [];
      const referencedContext = ((typeof ruleDef.condition === 'object') && ruleDef.condition.referencedContext) || [];
      if (referencedContext) {
        this.referencedVars = mergeArrays(referencedContext.generated, referencedContext.extra);
      }

      /**
       * Can either be an Expression or an Object of keys (constant exprs), each with an expression or a literal
       * value (primitives, arrays and object)
       * @type {undefined}
       */
      this.overlay = undefined;

      /**
       * set to true once the skipped overlay assignments are reported in the logs.
       * @private
       */
      this._skipOverlayCheckLog = false;
    }

    dispose() {
      // clear expressions
      this.overlay = undefined;
      this.condition = undefined;
    }

    /**
     * When there is a contextChange, additionally check the referencedContext against the changed variable or constant
     * before setting up condition expressions. If contextChange is undefined, referencedContext filtering is skipped.
     * @param {Object|undefined} contextChange variables and constants whose value changed
     * @returns {boolean} true when the rule should be considered for this context change
     */
    checkReferencedContext(contextChange) {
      if (contextChange) {
        const { name, namespace } = contextChange;
        if (this.referencedVars.length && !this.referencedVars.find((rv) => varNameMatch(rv, name, namespace))) {
          return false;
        }
      }
      return true;
    }

    /**
     * Sets up the condition and overlay expressions used by this rule for the current execution.
     *
     * A cloned rules context is prepared for conditions, augmented with any temporary contexts supplied by `$withEnv`.
     * Overlay expressions use the normal rules context plus the in-flight constants proxy. When `$withEnv` is used,
     * `$env.row` belongs in the condition only; reading `$env` while assigning an overlay value throws a configuration
     * error.
     *
     * @param {Object|undefined} contextChange change payload describing the
     *  variable/constant that triggered re-evaluation
     * @param {Object} liveRulesState live rules state object for the current rules execution
     * @param {Object} [liveRulesState.externalContexts] optional temporary contexts for condition expressions
     */
    setupExpressions(contextChange, liveRulesState) {
      const { externalContexts } = liveRulesState;
      const hasExternalContexts = !!(externalContexts && Object.keys(externalContexts).length);
      // Temporary contexts apply to this rules engine only.
      const conditionExpressionContexts = StateUtils.addExternalContexts(this.availableContexts, externalContexts);
      const overlayExpressionContexts = hasExternalContexts
        ? StateUtils.addExternalContexts(this.availableContexts, {
          $env: createInvalidOverlayEnvContext(this.id),
        }) : conditionExpressionContexts;
      let createProxy;
      let createConstantsProxy;
      const { container } = this;

      // shared setter throws error when any variable or constant is updated
      const sharedSetter = (expression) => () => {
        throw new Error(`The expression '${expression}' has attempted to mutate a variable from within `
          + ` the rule ${this.id}. This is not allowed. Please check your configuration.`);
      };

      // shared getter uses value of a constant from the local sandbox if available
      const sharedGetter = (pathh = [], doConstantsPropCheck = false, lrs = {}) => {
        let path = pathh;
        const { ruleConstants = {}, ruleValues = {} } = lrs;
        return (target, property) => {
          // JET's CSP expression evaluator probes Symbol(proxy) to unwrap its own proxies. Treat symbol keys as normal
          // property reads so they do not get folded into rule constant expression paths.
          if (typeof property === 'symbol') {
            return target[property];
          }

          let realValue = target[property];

          if (doConstantsPropCheck) {
            // when a single expression contains multiple occurrences of $constants.* or $page.constants.* etc.,
            // fixup the path, truncating anything following $constants or constants. Both generally can't occur in
            // a single traversal
            const constantsIndex = path.findIndex((p) => p === EXPRESSION_TOKEN_$CONSTANTS
              || p === EXPRESSION_TOKEN_CONSTANTS);
            path = path.slice(0, constantsIndex + 1);
            path = path.concat(property);
            const currentPropExpr = path.join('.');
            const propExprVariants = RulesUtils.exprsFromConstantName(container, property);

            // first check if a prior rule has already filled a value or has an expression that can provide a value.
            const rcExpr = Object.keys(ruleConstants).find((rc) => (rc === currentPropExpr))
              || propExprVariants.find((cp) => (cp === currentPropExpr));

            const hasRuleConstant = hasExternalContexts
              ? Object.prototype.hasOwnProperty.call(ruleConstants, propExprVariants[0])
              : ruleConstants[propExprVariants[0]];

            if (rcExpr && hasRuleConstant) {
              // generally ruleConstants are keyed by the $base.constants.* and maps to the literal value or an
              // expression. The ruleValues also use the same base expr to store resolved rule values
              const baseConstantExpr = propExprVariants[0];
              let ruleValue;
              try {
                ruleValue = hasExternalContexts && ruleValues[baseConstantExpr] !== undefined
                  ? ruleValues[baseConstantExpr]
                  : ruleValues[baseConstantExpr] || Utils.resolveIfObservable(ruleConstants[baseConstantExpr]);
              } catch (e) {
                // if expression fails log error and fall back to using store value.
                const msg = `An error occurred when evaluating the expression for ${currentPropExpr} used in the rule`
                + `${this.id}. This may be due to a recursive use of the constant that is being updated. The current`
                + 'store value will be used instead.';
                // warn because there may be unavoidable reasons to use the same constant on both lhs and rhs of a rule.
                this.log.warn(`${msg}\n`, e);
                ruleValue = realValue;
              } finally {
                this.log.fine('updating rules constant', rcExpr, 'with the value', ruleValue);
                ruleValues[baseConstantExpr] = ruleValue;
                // For `$withEnv` runs, return this rules run's value even when it is undefined; falling
                // back to the stored constant could leak a value calculated without the supplied `$env`.
                realValue = hasExternalContexts || ruleValue !== undefined ? ruleValue : realValue;
              }
            } else if (hasExternalContexts && propExprVariants.includes(currentPropExpr)) {
              const contexts = this.availableContexts;
              const { variable } = RulesUtils.getVariableFromReferenced(currentPropExpr, container, contexts);
              if (variable && typeof variable.isValueFromRules === 'function' && variable.isValueFromRules()) {
                realValue = undefined;
              }
            }
          }

          const config = Object.getOwnPropertyDescriptor(target, property);
          if (config && config.configurable === false && config.writable === false) {
            return realValue;
          }

          // TODO: this check skips setting up proxy for $base.<page|container>.constants.foo but does work for
          //  $base.constants.foo and other Object prototype variants. Unclear if this is intentional as this is
          //  also a problem with expressions setup in expression.js/preventWritesToStructures
          if (Utils.isObjectOrArray(realValue) && Utils.isPrototypeOfObject(realValue)) {
            path = (property === EXPRESSION_TOKEN_CONSTANTS) ? path.concat(property) : path;
            return property !== EXPRESSION_TOKEN_CONSTANTS
              ? createProxy(realValue, path) : createConstantsProxy(realValue, path);
          }
          return realValue;
        };
      };

      /**
       * Custom proxy object with special getters and setters for rules constants.
       * @param obj
       * @param expression
       * @param {string} key the root of the context obj
       * @returns {*}
       */
      const customRulesProxy = (obj, expression, key) => {
        // Wrap rule evaluation so $constants reads can see values produced earlier in the same rules run.
        // this proxy is set up only on 'constants' scope object
        const rulesConstantsListener = (path = []) => ({
          get: sharedGetter(path, true, liveRulesState),
          set: sharedSetter(expression),
        });

        const listener = (path) => ({
          get: sharedGetter(path),
          set: sharedSetter(expression),
        });

        createProxy = (toProxyObj, path) => new Proxy(toProxyObj, listener(path));
        createConstantsProxy = (toProxyObj, path) => new Proxy(toProxyObj, rulesConstantsListener(path));

        try {
          return (key === EXPRESSION_TOKEN_$CONSTANTS)
            ? createConstantsProxy(obj, [key]) : createProxy(obj, [key]);
        // eslint-disable-next-line no-unused-vars
        } catch (e) {
          return obj; // for IE11
        }
      };

      // When evaluating the condition or overlay, a custom proxy is set up that provides (rules eval-ed) values for
      // constants.
      /**
       * @param {string} exprString expression to compile
       * @param {Object} expressionContexts contexts to expose to the expression
       * @returns {Function} compiled expression
       */
      const getExpr = (exprString, expressionContexts) => {
        const expr = Expression.getExpression(exprString);
        // prohibitsWritesToStructure: true, sets writable to false, which disallows even reads of sub properties??
        return Expression.createFromString(expr, expressionContexts, true, customRulesProxy);
      };

      const { condition, overlay } = this.ruleDef;
      if (typeof condition !== 'undefined') {
        const conditionType = typeof condition;
        // func to set up condition expression with special proxy.
        const setupConditionExpr = (condExpr) => {
          if (Expression.isExpression(condExpr)) {
            this.condition = getExpr(condExpr, conditionExpressionContexts);
          } else {
            this.condition = coerceBoolean(condExpr);
          }
        };

        switch (conditionType) {
          case 'string':
            setupConditionExpr(condition);
            break;

          case 'boolean':
            this.condition = condition;
            break;

          case 'object':
            if (Utils.isObject(condition)) {
              const { expression } = condition;
              if (expression && this.checkReferencedContext(contextChange)) {
                setupConditionExpr(expression);
              }
            }
            break;

          default:
            this.log.warn('The condition set on the rule', this.id, 'is not an expression or an accepted boolean'
              + ' type. Condition is skipped!', condition);
            break;
        }
      }

      if (overlay) {
        if (typeof overlay === 'string') {
          const err = 'The overlay property must be an object map of constant expression to value (expressions).'
            + `Please check your configuration. ${overlay}`;
          this.log.error(err);
          throw new Error(err);
        } else if (Utils.isObjectType(overlay)) {
          const prunedOverlay = pruneValidTargets(overlay, this.targetExprs);
          if (Object.keys(prunedOverlay).length !== Object.keys(overlay).length) {
            const skippedKeys = Object.keys(overlay).filter((ok) => !prunedOverlay[ok]);
            if (!this._skipOverlayCheckLog) {
              this.log.info('The rule', this.id, 'evaluated values to constant(s) -', skippedKeys.join(','),
                'that are ignored. This can happen either due to an incorrect configuration, or when the current'
                  + ' extension\'s rule can provide a value(s) for the constant(s), but has a topmost / leaf'
                  + ' extension that extends the constant with a defaultValue. The default value is always used for'
                  + ' backwards compatibility.');
              this._skipOverlayCheckLog = true;
            }
          }

          // create constants for overlay property value expressions
          const targets = Object.keys(prunedOverlay);
          this.overlay = this.overlay || {};

          // Traverse the action parameters definition and create a constant in the action scope for each parameter
          targets.forEach((targetExpr) => {
            // process the value
            const propValue = prunedOverlay[targetExpr];
            const errMsg = `The overlay property '${targetExpr}' of rule ${this.id}, is incorrectly configured. It must`
            + ' have a value that is either an expression, a primitive, object or array literal with no nested'
            + ' expressions.';

            if (Expression.isExpression(propValue)) {
              this.overlay[targetExpr] = getExpr(propValue, overlayExpressionContexts);
            } else if (Utils.isPrimitive(propValue)) {
              this.overlay[targetExpr] = propValue;
            } else if (Utils.isObjectOrArray(propValue)) {
              const depExprs = StateUtils.getDepVarExpressions(targetExpr, propValue);
              if (depExprs.functions.length === 0) {
                this.overlay[targetExpr] = propValue;
              } else {
                this.log.error(errMsg);
              }
            } else {
              this.log.error(errMsg);
            }
          });
        }
      }
    }

    /**
     * Determines whether the rule needs to execute for the given context change. A default rule is one that a rules
     * engine manages and is one that simply executes the defaultValue expression. So this method returns true
     * always for that case.
     *
     * @param {Object} contextChange the variables and constants whose value changed causing a rule to be triggered
     * and re-evaluate
     * @return {boolean} true if rule can execute
     */
    canExecute(contextChange) {
      if (contextChange && !this.checkReferencedContext(contextChange)) {
        return false;
      }

      // always check condition, which is either a boolean or an expression
      if (typeof this.condition !== 'undefined') {
        if (typeof this.condition === 'boolean') {
          return this.condition;
        }
        const result = this.condition();
        if (result instanceof Promise) {
          const err = `The condition ${this.ruleDef.condition} set on the rule ${this.id} returned a Promise`
            + ', which is not supported. The expression must return a boolean.';
          throw Error(err);
        }

        return coerceBoolean(result);
      }

      return true;
    }

    /**
     * Checks if a rule is likely to run and returns the overlay configuration.
     *
     * @param {Object|undefined} contextChange change payload describing the
     *  variable/constant that triggered re-evaluation
     * @param {Object} liveRulesEngineState live rules state object shared across the current rules execution
     * @param {Object} [liveRulesEngineState.externalContexts] optional temporary contexts for condition setup
     * @return {Object} overlay configuration for this rule when it participates in the run; otherwise an empty object
     */
    prepareRun(contextChange, liveRulesEngineState) {
      // first check if rule is active
      const ruleStatus = this.ruleDef.status;

      // If rule is active it need not be set, or it needs to be exactly "active". rule is considered inactive if
      // status is explicitly set to 'inactive'.
      const active = (ruleStatus === undefined) || (ruleStatus === RULE_STATUS_ENUM.ACTIVE);
      if (active) {
        // setup expressions every time, before evaluating rule. It is needed to prime the availableContexts with the
        // eventPayload, which changes every time rules re-execute.
        this.setupExpressions(contextChange, liveRulesEngineState);
        if (this.canExecute(contextChange)) {
          return this.overlay;
        }
      } else if (ruleStatus !== RULE_STATUS_ENUM.INACTIVE) {
        // if something other than inactive - for instance it is some expression or a random value, log a warning
        const msg = `The 'status' property for the rule ${this.id} has a value ${ruleStatus} that is `
        + 'unsupported. Either use \'active\' or \'inactive\', or for conditional activation of rule, use the '
        + '\'condition\' property.';
        this.log.error(msg);
      }
      return {};
    }

    /**
     * Executes a rule and returns values for one or more interface constants
     *
     * @param {Object|undefined} contextChange variables and constants whose value changed, or undefined to run without
     *  referencedContext filtering
     * @param {Object} liveRulesState with props ruleSet and rulesConstants. This object is provided by caller
     * and is updated in situ with the intermediate results of the constants rules evaluation
     * @param {Object} liveRulesState.ruleValues map of constants and their rule evaluated values
     *
     * @returns {Object} evaluated values keyed by target expression, or an empty object when the rule should not run
     */
    run(contextChange, liveRulesState) {
      const { ruleValues } = liveRulesState;
      if (this.canExecute(contextChange)) {
        const retVals = {};
        Object.keys(this.overlay).forEach((op) => {
          const localOverlayProp = this.overlay[op];
          retVals[op] = Utils.resolveIfObservable(localOverlayProp);
          // it may be necessary to push rule values within a single rule into the liveRulesState
          ruleValues[op] = retVals[op];
        });
        return Object.fromEntries(Object.entries(retVals).filter((r) => r[1] !== undefined
          && this.targetExprs.includes(r[0])));
      }
      return {};
    }
  }

  return BasicRule;
});

